import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PaymentBadge } from "@/components/PaymentStatus";
import type { LegacyLineItem, UnitSessionRow } from "@/lib/unitSessions";
import { durationQualityNote } from "@/lib/sessionDurations";
import {
  FormatInputError,
  formatDuration,
  formatEnergy,
  formatMoneyMajor,
  isLegacyRateUnrepresentable,
  minorToDecimalString,
  moneyV2Display,
  rateV2Display,
  signedMoneyV2Display,
  type InvoiceLineV2,
  type InvoiceMoneyV2,
  type MoneyV2,
} from "@voltie/localization-web";
import { useT, type Translate } from "@/app/localization";
import { energyFromWh, viewerInstant } from "@/app/displayFormat";

export type HistorySession = UnitSessionRow & {
  // Client-only: set when the payment projection failed to load for this row's
  // page (response payment_available === false) so the dialog can show
  // "Unavailable" instead of silently omitting the amounts.
  paymentUnavailable?: boolean;
};

function durationText(seconds?: number | null): string {
  if (seconds == null || seconds < 0) return "—";
  return formatDuration(seconds, "seconds");
}

export interface DurationField {
  /** Stable row identity; the label is display text only. */
  id: "plugged_in" | "charging" | "idle" | "suspended";
  label: string;
  value: string;
  sub?: string;
}

/** Plugged-in / charging / idle / suspended rows for the dialog. Idle is only ever the
 *  recorded idle — never plugged-in minus charging — and a value the backend could not
 *  establish shows "—", not the number kept for API compatibility. */
export function historyDurationFields(t: Translate, s: HistorySession): DurationField[] {
  const plugged: DurationField = {
    id: "plugged_in",
    label: t("operator.session_detail.plugged_in_time"),
    value: durationText(s.duration_seconds),
  };
  const chargingLabel = t("operator.session_detail.charging_time");
  const breakdown = s.duration_breakdown;
  if (breakdown == null) {
    // Older payload: the fields it always had, no invented breakdown.
    return [plugged, { id: "charging", label: chargingLabel, value: durationText(s.charging_seconds) }];
  }
  const quality = durationQualityNote(breakdown, s.duration_issues, t)?.label;
  const notRecorded = t("operator.session_detail.not_recorded");
  const idleLabel = t("operator.session_detail.idle_time");
  const charging: DurationField = breakdown === "unknown"
    ? { id: "charging", label: chargingLabel, value: "—", sub: notRecorded }
    : { id: "charging", label: chargingLabel, value: durationText(s.charging_seconds), sub: quality };
  const idle: DurationField = s.idle_seconds == null
    ? { id: "idle", label: idleLabel, value: "—", sub: notRecorded }
    : { id: "idle", label: idleLabel, value: durationText(s.idle_seconds), sub: quality };
  const fields: DurationField[] = [plugged, charging, idle];
  if (s.suspended_seconds != null && s.suspended_seconds > 0) {
    fields.push({ id: "suspended", label: t("operator.session_detail.suspended_time"), value: durationText(s.suspended_seconds), sub: quality });
  }
  return fields;
}

/** Line items are keyed by fee type and unit of measure; unknown pairs keep the raw fee type. */
const FEE_KEY: Record<string, string> = {
  "charging|kwh": "billing.line_item.fee_energy",
  "charging|hour": "billing.line_item.fee_charging_time",
  "idle|hour": "billing.line_item.fee_idle",
  "activation|session": "billing.line_item.fee_activation",
};

function feeLabel(t: Translate, li: LegacyLineItem): string {
  const key = FEE_KEY[`${li.fee_type}|${li.uom}`];
  return key ? t(key) : li.fee_type;
}

function qtyText(li: LegacyLineItem): string {
  if (li.energy_kwh != null) return formatEnergy(li.energy_kwh, "kWh", "meter");
  if (li.hours != null) return durationText(li.hours * 3600);
  return "";
}

/** A D-T2-1 legacy USD line amount (fixed hundredths) or rate (major units), shown like the row's v2 amounts; a value the formatter refuses is shown as sent. */
function legacyMoney(major: () => number | string, currency: string, raw: number, fractionDigits?: number): string {
  try {
    return formatMoneyMajor(major(), currency, { display: "code", maximumFractionDigits: fractionDigits });
  } catch (e) {
    if (e instanceof FormatInputError) return `${raw} ${currency}`;
    throw e;
  }
}

/** Invoice roles with a line-item label of their own; the others are cases of operator.session_detail.invoice_line. */
const ROLE_KEY: Partial<Record<InvoiceLineV2["role"], string>> = {
  activation: "billing.line_item.fee_activation",
  energy: "billing.line_item.fee_energy",
  charging_time: "billing.line_item.fee_charging_time",
  idle: "billing.line_item.fee_idle",
};

function roleLabel(t: Translate, line: InvoiceLineV2): string {
  const key = ROLE_KEY[line.role];
  return key ? t(key) : t("operator.session_detail.invoice_line", { line: line.role });
}

/** Legacy rates are binary floats in major units (0.125/kWh); show their digits instead of rounding to cents. */
const LEGACY_RATE_DIGITS = 6;

const RATE_UOM = { kWh: "kwh", hour: "hour", session: "session" } as const;

/** Quantity and rate of a v2 invoice line: the quantity is a measurement, the rate keeps every server digit. */
function invoiceLineDetail(t: Translate, line: InvoiceLineV2, exponent: number): string {
  const rate = line.rate === null ? null : rateV2Display(line.rate, { minimumFractionDigits: exponent });
  const parts: string[] = [];
  if (line.quantity !== null && rate?.unit === "kWh") parts.push(formatEnergy(Number(line.quantity), "kWh", "meter"));
  if (line.quantity !== null && rate?.unit === "hour") parts.push(durationText(Number(line.quantity) * 3600));
  if (rate !== null) parts.push(t("billing.line_item.rate", { rate: rate.amount, uom: RATE_UOM[rate.unit] }));
  return parts.join(" ");
}

const isFreeRate = (line: InvoiceLineV2) => line.rate !== null && !isLegacyRateUnrepresentable(line.rate) && line.rate.amount === "0";

const SOURCE_VALUES = new Set(["pricing_profile", "unit_default"]);
const AUTHORIZED_VALUES = new Set(["admin", "customer", "auto"]);

function sourceLabel(t: Translate, source?: string | null): string | null {
  return source && SOURCE_VALUES.has(source) ? t("operator.session_detail.pricing_source", { source }) : null;
}

function authorizedLabel(t: Translate, by?: string): string | null {
  return by && AUTHORIZED_VALUES.has(by) ? t("operator.session_detail.authorized_by", { by }) : null;
}

/**
 * Read-only detail dialog for one completed session from the device-page
 * history. Renders the finalized per-interval billing line items (paid AND
 * free segments) — no tariff math happens here.
 */
export function HistorySessionDetail({ session, showCosts, onClose }: {
  session: HistorySession | null;
  showCosts: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const s = session;
  const money = s?.money.status === "value" ? s.money : null;
  const unavailable = t("common.state.unavailable");
  // The money_v2 block always names the session's currency; legacy lines exist only beside it.
  const currency = money?.history.currency ?? "";
  const total = money?.history.total ?? null;
  const invoice = money?.history.invoice ?? null;
  const items = money?.legacy_usd_lines ?? [];
  const notFinalized = money?.history.billing_source === "pending" || money?.history.billing_source === "failed";
  const notAuthorized = s?.billing_status === "not_authorized";
  // Finalized, but no invoice could be read: the amount stays unknown (never a 0).
  const invoiceMissing = s?.billing_status === "finalized";
  const totalText = (amount: MoneyV2 | null) => (amount === null ? null : (moneyV2Display(amount) ?? unavailable));
  const totalShown = totalText(total);
  const billed = !money
    ? unavailable
    : totalShown
      ?? (notFinalized
        ? t("billing.state.not_finalized")
        : notAuthorized
          ? t("billing.state.not_authorized")
          : invoiceMissing
            ? t("billing.state.invoice_missing")
            : "—");
  const captured = !s?.payment
    ? unavailable
    : !money
      ? unavailable
      : (totalText(money.payment?.captured ?? null) ?? "—");
  const durationNote = s ? durationQualityNote(s.duration_breakdown, s.duration_issues, t) : null;

  return (
    <Dialog open={!!s} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        {s && (
          <>
            <DialogHeader>
              <DialogTitle>{t("operator.session.details_title")}</DialogTitle>
              <DialogDescription className="font-mono text-xs">{s.session_id}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Timing + energy overview */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <DetailItem label={t("operator.session_detail.start")} value={viewerInstant(s.start_time) ?? "—"} />
                <DetailItem label={t("operator.session_detail.end")} value={viewerInstant(s.end_time) ?? "—"} />
                {historyDurationFields(t, s).map((f) => (
                  <DetailItem key={f.id} label={f.label} value={f.value} sub={f.sub} />
                ))}
                <DetailItem label={t("operator.session_detail.energy_delivered")} value={energyFromWh(s.energy_delivered) ?? "—"} />
                {showCosts && (
                  <DetailItem label={t("billing.amount.billed")} value={billed} />
                )}
                {showCosts && (s.payment || s.paymentUnavailable) && (
                  <DetailItem label={t("billing.amount.captured")} value={captured} />
                )}
                {showCosts && (s.payment || s.paymentUnavailable) && (
                  <div>
                    <div className="text-xs opacity-60">{t("operator.session_history.column_payment")}</div>
                    <div className="mt-0.5">
                      {s.payment ? (
                        <PaymentBadge
                          state={s.payment.payment_state}
                          needsAttention={s.payment.payment_needs_attention}
                        />
                      ) : (
                        <span className="text-xs opacity-60">{t("common.state.unavailable")}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {durationNote && (
                <div className="text-xs opacity-70" data-testid="duration-quality-note">
                  {durationNote.detail}
                </div>
              )}

              <Separator />

              {/* Who started it + which tariff applied */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <DetailItem
                  label={t("operator.session_detail.started_by")}
                  value={s.customer_name || authorizedLabel(t, s.authorized_by) || s.authorized_by || "—"}
                  sub={s.customer_email ?? undefined}
                />
                <DetailItem
                  label={t("operator.session_detail.tariff")}
                  value={s.pricing_profile_name
                    || sourceLabel(t, s.pricing_source)
                    || (s.pricing_source ?? "—")}
                  sub={s.pricing_profile_name ? (sourceLabel(t, s.pricing_source) ?? undefined) : undefined}
                />
              </div>

              {/* Per-interval rate breakdown — paid AND free segments */}
              {showCosts && (
                <>
                  <Separator />
                  <div>
                    <div className="text-xs font-semibold uppercase opacity-70 mb-2">{t("operator.session_detail.rate_intervals")}</div>
                    {!money ? (
                      <div className="text-sm opacity-60">{unavailable}</div>
                    ) : invoice ? (
                      <InvoiceLines t={t} invoice={invoice} total={totalShown} />
                    ) : items.length > 0 ? (
                      <div className="space-y-2" data-testid="session-line-items">
                        {items.map((li, i) => (
                          <div key={i} className="flex items-start justify-between gap-3 text-sm">
                            <div>
                              <div className="flex items-center gap-2">
                                <span>{feeLabel(t, li)}</span>
                                {li.rate === 0 ? (
                                  <Badge variant="success">{t("billing.line_item.free")}</Badge>
                                ) : li.billable === false ? (
                                  <Badge variant="secondary">{t("billing.line_item.not_charged")}</Badge>
                                ) : null}
                              </div>
                              <div className="text-xs opacity-60">
                                {li.start_at && li.end_at
                                  ? t("billing.line_item.interval", {
                                      start: viewerInstant(li.start_at, "time") ?? "",
                                      end: viewerInstant(li.end_at, "time") ?? "",
                                    })
                                  : null}
                                {qtyText(li) ? ` · ${qtyText(li)}` : null}
                                {li.rate > 0
                                  ? ` ${t("billing.line_item.rate", { rate: legacyMoney(() => li.rate, currency, li.rate, LEGACY_RATE_DIGITS), uom: li.uom })}`
                                  : null}
                              </div>
                            </div>
                            <div className="tabular-nums font-medium shrink-0">
                              {legacyMoney(() => minorToDecimalString(li.amount_cents, 2), currency, li.amount_cents)}
                            </div>
                          </div>
                        ))}
                        {totalShown !== null && <TotalLine label={t("billing.line_item.total")} value={totalShown} />}
                      </div>
                    ) : (
                      <div className="text-sm opacity-60">
                        {notFinalized
                          ? t("operator.session_detail.billing_not_finalized")
                          : t("operator.session_detail.no_breakdown")}
                      </div>
                    )}
                  </div>
                </>
              )}

              {s.ended_by && (
                <div className="text-xs opacity-60">{t("operator.session_detail.ended_by", { reason: s.ended_by })}</div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** The lines of a finalized v2 invoice as stored: role, quantity, rate and amount; nothing is summed here. */
function InvoiceLines({ t, invoice, total }: { t: Translate; invoice: InvoiceMoneyV2; total: string | null }) {
  const adjustment = invoice.capture_adjustment.amount_minor !== "0";
  return (
    <div className="space-y-2" data-testid="session-line-items">
      {invoice.lines.map((line, i) => (
        <div key={i} className="flex items-start justify-between gap-3 text-sm">
          <div>
            <div className="flex items-center gap-2">
              <span>{roleLabel(t, line)}</span>
              {isFreeRate(line) && <Badge variant="success">{t("billing.line_item.free")}</Badge>}
            </div>
            <div className="text-xs opacity-60">{invoiceLineDetail(t, line, invoice.exponent)}</div>
          </div>
          <div className="tabular-nums font-medium shrink-0">
            {signedMoneyV2Display(line.amount) ?? t("common.state.unavailable")}
          </div>
        </div>
      ))}
      {adjustment && (
        <div className="flex items-start justify-between gap-3 text-sm">
          <span>{t("operator.session_detail.invoice_line", { line: "capture_adjustment" })}</span>
          <div className="tabular-nums font-medium shrink-0">
            {signedMoneyV2Display(invoice.capture_adjustment) ?? t("common.state.unavailable")}
          </div>
        </div>
      )}
      {total !== null && <TotalLine label={t("billing.line_item.total")} value={total} />}
    </div>
  );
}

function TotalLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-t border-border pt-2 text-sm font-semibold">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function DetailItem({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="text-xs opacity-60">{label}</div>
      <div className="font-semibold">{value}</div>
      {sub && <div className="text-xs opacity-60">{sub}</div>}
    </div>
  );
}
